/*
  <tab-panels> — WAI-ARIA tabs with automatic activation (styles: .tab-list* and
  .tab-panel in component-base.css).

  Markup:
    <tab-panels>
      <div class="tab-list" role="tablist" aria-label="…">
        <button type="button" class="tab-list__tab button-reset" role="tab" id="Tab-ID-1"
          aria-controls="TabPanel-ID-1" aria-selected="true">…</button>
        <button type="button" class="tab-list__tab button-reset" role="tab" id="Tab-ID-2"
          aria-controls="TabPanel-ID-2" aria-selected="false" tabindex="-1">…</button>
      </div>
      <div class="tab-panel" role="tabpanel" id="TabPanel-ID-1" aria-labelledby="Tab-ID-1">…</div>
      <div class="tab-panel" role="tabpanel" id="TabPanel-ID-2" aria-labelledby="Tab-ID-2" hidden>…</div>
    </tab-panels>

  Liquid renders every panel, so all links are in the HTML for crawlers; inactive panels
  carry [hidden], so their lazy images are not requested until the tab is opened. Without
  JavaScript (.no-js) every panel shows and the tab list is hidden (component-base.css).

  Keyboard: Left/Right arrows move between tabs (wrapping), Home/End jump to the ends.
  Dispatches `tab-panels:change` (bubbles) with detail { tab, panel }.
  Theme editor: selecting a tab's block, or a block inside a panel, opens that tab.
*/
class TabPanels extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]')).filter((tab) => tab.closest('tab-panels') === this);
    if (!this.tabs.length) return;

    this.handleClick = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab && this.tabs.includes(tab)) this.select(tab);
    };
    this.handleKeydown = (event) => this.onKeydown(event);
    this.handleBlockSelect = (event) => this.onBlockSelect(event.target);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    const current = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.select(current, { silent: true });
  }

  disconnectedCallback() {
    if (!this.tabs || !this.tabs.length) return;
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  panelFor(tab) {
    return document.getElementById(tab.getAttribute('aria-controls'));
  }

  select(tab, { focus = false, silent = false } = {}) {
    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panel = this.panelFor(item);
      if (panel) panel.hidden = !selected;
    });

    if (focus) tab.focus();
    if (silent) return;

    this.scrollTabIntoView(tab);
    this.dispatchEvent(new CustomEvent('tab-panels:change', { bubbles: true, detail: { tab, panel: this.panelFor(tab) } }));
  }

  // Keeps the active tab visible in a horizontally scrolling tab list without moving the page.
  scrollTabIntoView(tab) {
    const list = tab.parentElement;
    if (!list || list.scrollWidth <= list.clientWidth) return;

    const start = tab.offsetLeft;
    const end = start + tab.offsetWidth;
    if (start < list.scrollLeft) {
      list.scrollTo({ left: start - 8 });
    } else if (end > list.scrollLeft + list.clientWidth) {
      list.scrollTo({ left: end - list.clientWidth + 8 });
    }
  }

  onKeydown(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.tabs.includes(tab)) return;

    const index = this.tabs.indexOf(tab);
    const last = this.tabs.length - 1;
    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = this.tabs[index === last ? 0 : index + 1];
        break;
      case 'ArrowLeft':
        next = this.tabs[index === 0 ? last : index - 1];
        break;
      case 'Home':
        next = this.tabs[0];
        break;
      case 'End':
        next = this.tabs[last];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, { focus: true });
  }

  onBlockSelect(target) {
    if (!(target instanceof Element) || !this.contains(target)) return;

    const tab = this.tabs.find((item) => {
      const panel = this.panelFor(item);
      return item.contains(target) || (panel && panel.contains(target));
    });
    if (tab) this.select(tab);
  }
}

if (!customElements.get('tab-panels')) {
  customElements.define('tab-panels', TabPanels);
}
